import React from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  spring,
} from 'remotion';

interface PermaElement {
  name: string;
  english: string;
  desc: string;
  icon: string;
  color: string;
}

interface HappinessIntervention {
  name: string;
  english: string;
  desc: string;
  icon: string;
  color: string;
}

interface ResearchFinding {
  name: string;
  desc: string;
  icon: string;
}

interface PositivePsychTemplateProps {
  topic: string;
  subtitle: string;
  permaModel: PermaElement[];
  interventions: HappinessIntervention[];
  findings: ResearchFinding[];
  radarDimensions: { label: string; value: number }[];
  stats: { value: string; label: string }[];
  bottomQuote: string;
}

const DEFAULT_PROPS: PositivePsychTemplateProps = {
  topic: '积极心理学与幸福科学',
  subtitle: 'Positive Psychology & Science of Happiness',
  permaModel: [
    {
      name: '积极情绪',
      english: 'Positive Emotions',
      desc: '培养快乐、感恩、希望等正面情感',
      icon: '😊',
      color: '#f59e0b',
    },
    {
      name: '投入',
      english: 'Engagement',
      desc: '全身心沉浸于有挑战性的活动中',
      icon: '🎯',
      color: '#ef4444',
    },
    {
      name: '关系',
      english: 'Relationships',
      desc: '建立深层的人际联结与归属感',
      icon: '🤝',
      color: '#ec4899',
    },
    {
      name: '意义',
      english: 'Meaning',
      desc: '服务于超越自我的更大目标',
      icon: '🌟',
      color: '#8b5cf6',
    },
    {
      name: '成就',
      english: 'Accomplishment',
      desc: '追求卓越并体验掌握感',
      icon: '🏆',
      color: '#10b981',
    },
  ],
  interventions: [
    {
      name: '感恩日记',
      english: 'Gratitude Journal',
      desc: '每天记录三件感恩的事',
      icon: '📝',
      color: '#f59e0b',
    },
    {
      name: '心流体验',
      english: 'Flow Experience',
      desc: '寻找技能与挑战匹配的活动',
      icon: '🌊',
      color: '#3b82f6',
    },
    {
      name: '善行练习',
      english: 'Acts of Kindness',
      desc: '有意识地为他人做善事',
      icon: '💝',
      color: '#ec4899',
    },
    {
      name: '正念冥想',
      english: 'Mindfulness',
      desc: '不加评判地觉察当下体验',
      icon: '🧘',
      color: '#10b981',
    },
  ],
  findings: [
    { name: '享乐适应', desc: '人们对积极事件的快乐感会随时间消退', icon: '📉' },
    { name: '社交联结', desc: '人际关系是幸福感最强预测因素', icon: '👥' },
    { name: '心流状态', desc: '高挑战高技能时体验最佳状态', icon: '⚡' },
    { name: '意义胜于快乐', desc: '有意义的生活比快乐的生活满足感更持久', icon: '🔑' },
  ],
  radarDimensions: [
    { label: '积极情绪', value: 75 },
    { label: '投入', value: 82 },
    { label: '关系', value: 68 },
    { label: '意义', value: 78 },
    { label: '成就', value: 85 },
  ],
  stats: [
    { value: '$75K', label: '收入对幸福感的边际效用拐点' },
    { value: '40%', label: '幸福感可由主观行为影响' },
    { value: '8周', label: '正念冥想显著改变大脑结构' },
  ],
  bottomQuote:
    '幸福不是终点，而是一种行走方式——积极心理学告诉我们，幸福是可以被科学地培养和提升的',
};

export const PositivePsychTemplate: React.FC<
  Partial<PositivePsychTemplateProps>
> = (props) => {
  const p = { ...DEFAULT_PROPS, ...props };
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const headerIn = spring({
    frame,
    fps,
    config: { damping: 18, stiffness: 80 },
  });

  const permaStart = 30;
  const interventionStart = 160;
  const radarStart = 200;
  const findingStart = 350;
  const statsStart = 440;
  const quoteStart = 500;

  const quoteOpacity = interpolate(frame, [quoteStart, quoteStart + 30], [0, 1], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });

  const radarDraw = interpolate(frame, [radarStart, radarStart + 100], [0, 1], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });

  const radarCenter = { x: 250, y: 200 };
  const radarRadius = 130;
  const dims = p.radarDimensions;
  const radarPoints = dims.map((d, i) => {
    const angle = (i / dims.length) * 2 * Math.PI - Math.PI / 2;
    const r = (d.value / 100) * radarRadius * radarDraw;
    return {
      x: radarCenter.x + r * Math.cos(angle),
      y: radarCenter.y + r * Math.sin(angle),
      labelX: radarCenter.x + (radarRadius + 30) * Math.cos(angle),
      labelY: radarCenter.y + (radarRadius + 30) * Math.sin(angle),
      ...d,
    };
  });
  const radarPath = radarPoints
    .map((pt, i) => `${i === 0 ? 'M' : 'L'}${pt.x},${pt.y}`)
    .join(' ') + ' Z';

  return (
    <AbsoluteFill
      style={{
        background: 'linear-gradient(180deg, #2a1408 0%, #1a0c04 100%)',
        fontFamily: '-apple-system, "PingFang SC", "Noto Sans SC", sans-serif',
        color: 'white',
        overflow: 'hidden',
        width: 1920,
        height: 1080,
      }}
    >
      {/* Floating sun-like particles */}
      {Array.from({ length: 50 }).map((_, i) => {
        const x = (i * 409) % 1920;
        const y = (i * 199) % 1080;
        const size = 4 + (i % 6) * 2;
        const drift = Math.sin(frame * 0.025 + i * 1.0) * 8;
        const driftX = Math.cos(frame * 0.02 + i * 0.8) * 6;
        const pulse = 0.06 + 0.05 * Math.sin(frame * 0.04 + i * 0.6);
        const colors = ['#f59e0b', '#fbbf24', '#ea580c', '#fcd34d'];
        return (
          <div
            key={`p-${i}`}
            style={{
              position: 'absolute',
              left: x + driftX,
              top: y + drift,
              width: size,
              height: size,
              borderRadius: '50%',
              background: `radial-gradient(circle, ${colors[i % colors.length]}, transparent)`,
              opacity: pulse,
              boxShadow: `0 0 ${size * 4}px ${colors[i % colors.length]}30`,
            }}
          />
        );
      })}

      {/* ===== Header ===== */}
      <div
        style={{
          position: 'absolute',
          top: 28,
          left: 44,
          right: 44,
          opacity: headerIn,
          transform: `translateY(${(1 - headerIn) * 20}px)`,
          display: 'flex',
          alignItems: 'center',
          gap: 20,
        }}
      >
        <div
          style={{
            padding: '6px 18px',
            background: '#ea580c',
            borderRadius: 8,
            fontSize: 24,
            fontWeight: 700,
            color: '#fef3c7',
            flexShrink: 0,
          }}
        >
          ☀️ 积极心理
        </div>
        <h1
          style={{
            fontSize: 46,
            fontWeight: 900,
            margin: 0,
            color: '#fef3c7',
            whiteSpace: 'nowrap',
          }}
        >
          {p.topic}
        </h1>
        <p
          style={{
            fontSize: 24,
            color: '#94a3b8',
            margin: 0,
            whiteSpace: 'nowrap',
          }}
        >
          {p.subtitle}
        </p>
      </div>

      {/* ===== LEFT COLUMN: PERMA Model ===== */}
      <div style={{ position: 'absolute', top: 115, left: 36, width: 460 }}>
        <div
          style={{
            fontSize: 26,
            color: '#94a3b8',
            marginBottom: 20,
            letterSpacing: '0.08em',
          }}
        >
          PERMA 幸福模型
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {p.permaModel.map((el, i) => {
            const s = spring({
              frame: frame - (permaStart + i * 16),
              fps,
              config: { damping: 14, stiffness: 120 },
            });
            return (
              <div
                key={i}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 14,
                  background: '#1a0c04',
                  borderRadius: 14,
                  padding: '10px 16px',
                  border: `1.5px solid ${el.color}30`,
                  opacity: s,
                  transform: `translateX(${(1 - s) * 30}px)`,
                }}
              >
                <div
                  style={{
                    width: 48,
                    height: 48,
                    borderRadius: '50%',
                    background: `${el.color}20`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 30,
                    flexShrink: 0,
                    boxShadow: `0 0 18px ${el.color}30`,
                  }}
                >
                  {el.icon}
                </div>
                <div style={{ flex: 1 }}>
                  <div
                    style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}
                  >
                    <span
                      style={{
                        fontSize: 20,
                        fontWeight: 900,
                        color: el.color,
                        background: `${el.color}20`,
                        padding: '1px 8px',
                        borderRadius: 4,
                      }}
                    >
                      {el.english.charAt(0)}
                    </span>
                    <span
                      style={{ fontSize: 24, fontWeight: 800, color: el.color }}
                    >
                      {el.name}
                    </span>
                    <span style={{ fontSize: 20, color: '#64748b' }}>
                      {el.english}
                    </span>
                  </div>
                  <div style={{ fontSize: 18, color: '#94a3b8', marginTop: 2 }}>
                    {el.desc}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Happiness Interventions */}
        <div style={{ marginTop: 20 }}>
          <div
            style={{
              fontSize: 26,
              color: '#94a3b8',
              marginBottom: 10,
              letterSpacing: '0.08em',
            }}
          >
            幸福干预策略
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            {p.interventions.map((inv, i) => {
              const s = spring({
                frame: frame - (interventionStart + i * 16),
                fps,
                config: { damping: 14, stiffness: 120 },
              });
              return (
                <div
                  key={i}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    background: '#1a0c04',
                    borderRadius: 12,
                    padding: '10px 12px',
                    border: `1.5px solid ${inv.color}30`,
                    opacity: s,
                    transform: `translateX(${(1 - s) * 24}px)`,
                  }}
                >
                  <div style={{ fontSize: 24, flexShrink: 0 }}>{inv.icon}</div>
                  <span
                    style={{
                      fontSize: 24,
                      fontWeight: 800,
                      color: inv.color,
                    }}
                  >
                    {inv.name}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* ===== CENTER: Wellbeing Radar Chart SVG ===== */}
      <div style={{ position: 'absolute', top: 115, left: 520, width: 900 }}>
        <div
          style={{
            fontSize: 26,
            color: '#94a3b8',
            marginBottom: 10,
            letterSpacing: '0.08em',
          }}
        >
          幸福感雷达图
        </div>
        <svg width="860" height="690" viewBox="0 0 500 400">
          {/* Grid rings */}
          {[0.25, 0.5, 0.75, 1].map((scale, si) => {
            const points = dims
              .map((_, i) => {
                const angle = (i / dims.length) * 2 * Math.PI - Math.PI / 2;
                const r = radarRadius * scale;
                return `${radarCenter.x + r * Math.cos(angle)},${radarCenter.y + r * Math.sin(angle)}`;
              })
              .join(' ');
            return (
              <polygon
                key={si}
                points={points}
                fill="none"
                stroke="#ea580c"
                strokeWidth="1"
                opacity={0.15}
              />
            );
          })}

          {/* Axis lines */}
          {dims.map((_, i) => {
            const angle = (i / dims.length) * 2 * Math.PI - Math.PI / 2;
            return (
              <line
                key={i}
                x1={radarCenter.x}
                y1={radarCenter.y}
                x2={radarCenter.x + radarRadius * Math.cos(angle)}
                y2={radarCenter.y + radarRadius * Math.sin(angle)}
                stroke="#ea580c"
                strokeWidth="1"
                opacity="0.2"
              />
            );
          })}

          {/* Data polygon */}
          <polygon
            points={radarPoints.map((pt) => `${pt.x},${pt.y}`).join(' ')}
            fill="#f59e0b"
            fillOpacity={0.15 * radarDraw}
            stroke="#f59e0b"
            strokeWidth="2.5"
            opacity={radarDraw}
          />

          {/* Data points and labels */}
          {radarPoints.map((pt, i) => {
            const dotS = spring({
              frame: frame - (radarStart + 30 + i * 14),
              fps,
              config: { damping: 14, stiffness: 120 },
            });
            return (
              <g key={i} opacity={dotS}>
                <circle cx={pt.x} cy={pt.y} r="5" fill="#fbbf24" stroke="#fef3c7" strokeWidth="2" />
                <text
                  x={pt.labelX}
                  y={pt.labelY - 6}
                  fill="#fef3c7"
                  fontSize="15"
                  fontWeight="700"
                  textAnchor="middle"
                >
                  {pt.label}
                </text>
                <text
                  x={pt.labelX}
                  y={pt.labelY + 12}
                  fill="#f59e0b"
                  fontSize="13"
                  fontWeight="700"
                  textAnchor="middle"
                >
                  {pt.value}%
                </text>
              </g>
            );
          })}

          {/* Happiness formula */}
          <g
            opacity={interpolate(frame, [radarStart + 80, radarStart + 100], [0, 1], {
              extrapolateLeft: 'clamp',
              extrapolateRight: 'clamp',
            })}
          >
            <text x="250" y="368" fill="#94a3b8" fontSize="14" textAnchor="middle">
              H = S + C + V （幸福 = 基因设定 + 生活条件 + 主观活动）
            </text>
            <text x="250" y="390" fill="#64748b" fontSize="13" textAnchor="middle">
              50% 基因 ｜ 10% 环境 ｜ 40% 主观行为
            </text>
          </g>
        </svg>
      </div>

      {/* ===== RIGHT COLUMN: Research Findings ===== */}
      <div style={{ position: 'absolute', top: 115, right: 36, width: 380 }}>
        <div
          style={{
            fontSize: 26,
            color: '#94a3b8',
            marginBottom: 20,
            letterSpacing: '0.08em',
          }}
        >
          关键研究发现
        </div>
        {p.findings.map((f, i) => {
          const s = spring({
            frame: frame - (findingStart + i * 16),
            fps,
            config: { damping: 14, stiffness: 120 },
          });
          return (
            <div
              key={i}
              style={{
                background: '#1a0c04',
                borderRadius: 12,
                padding: '14px 16px',
                border: '1.5px solid #ea580c30',
                marginBottom: 8,
                opacity: s,
                transform: `translateY(${(1 - s) * 16}px)`,
                display: 'flex',
                alignItems: 'center',
                gap: 12,
              }}
            >
              <div
                style={{
                  width: 46,
                  height: 46,
                  borderRadius: 10,
                  background: '#ea580c20',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 26,
                  flexShrink: 0,
                }}
              >
                {f.icon}
              </div>
              <div>
                <div style={{ fontSize: 24, fontWeight: 800, color: '#fbbf24' }}>
                  {f.name}
                </div>
                <div style={{ fontSize: 16, color: '#94a3b8', marginTop: 2 }}>
                  {f.desc}
                </div>
              </div>
            </div>
          );
        })}

        {/* Wellbeing Dimensions */}
        <div style={{ marginTop: 20 }}>
          <div
            style={{
              fontSize: 26,
              color: '#94a3b8',
              marginBottom: 10,
              letterSpacing: '0.08em',
            }}
          >
            幸福感提升方法
          </div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 8,
            }}
          >
            {[
              { label: '品味当下', icon: '🌸' },
              { label: '优势运用', icon: '💪' },
              { label: '乐观训练', icon: '🌈' },
              { label: '目标设定', icon: '🎯' },
            ].map((item, i) => {
              const s = spring({
                frame: frame - (findingStart + 80 + i * 12),
                fps,
                config: { damping: 14, stiffness: 130 },
              });
              return (
                <div
                  key={i}
                  style={{
                    background: '#1a0c04',
                    borderRadius: 12,
                    padding: '14px 10px',
                    textAlign: 'center',
                    border: '1px solid #ea580c30',
                    opacity: s,
                    transform: `scale(${0.8 + 0.2 * s})`,
                  }}
                >
                  <div style={{ fontSize: 26, marginBottom: 4 }}>{item.icon}</div>
                  <div style={{ fontSize: 20, fontWeight: 700, color: '#fbbf24' }}>
                    {item.label}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* ===== Stats (bottom bar) ===== */}
      <div
        style={{
          position: 'absolute',
          bottom: 105,
          left: 36,
          right: 36,
          display: 'flex',
          gap: 20,
        }}
      >
        {p.stats.map((st, i) => {
          const stIn = spring({
            frame: frame - (statsStart + i * 14),
            fps,
            config: { damping: 14, stiffness: 100 },
          });
          return (
            <div
              key={i}
              style={{
                flex: 1,
                background: '#1a0c04',
                borderRadius: 14,
                padding: '14px 10px',
                textAlign: 'center',
                border: '1px solid #ea580c30',
                opacity: stIn,
                transform: `scale(${0.8 + 0.2 * stIn})`,
              }}
            >
              <div style={{ fontSize: 34, fontWeight: 900, color: '#fbbf24' }}>
                {st.value}
              </div>
              <div style={{ fontSize: 18, color: '#94a3b8', marginTop: 4 }}>
                {st.label}
              </div>
            </div>
          );
        })}
      </div>

      {/* ===== Bottom Quote ===== */}
      <div
        style={{
          position: 'absolute',
          bottom: 20,
          left: 36,
          right: 36,
          background: '#1a0c04',
          borderRadius: 14,
          padding: '14px 22px',
          borderTop: '3px solid #ea580c',
          opacity: quoteOpacity,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 22 }}>☀️</span>
          <span style={{ fontSize: 20, color: '#fef3c7', lineHeight: 1.6 }}>
            {p.bottomQuote}
          </span>
        </div>
      </div>

      {/* Progress bar */}
      <div
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          width: `${interpolate(frame, [0, 540], [0, 100], {
            extrapolateLeft: 'clamp',
            extrapolateRight: 'clamp',
          })}%`,
          height: 4,
          background: 'linear-gradient(90deg, #ea580c, #f59e0b, #fef3c7)',
        }}
      />
    </AbsoluteFill>
  );
};
